import { Injectable } from '@angular/core';
import { JdData } from './common-interfaces-classes/JdData';
import { SessionStorageService } from './_services/session-storage.service';
import { RedirectData } from './common-interfaces-classes/RedirectData';
import { GeneralConstants } from './constants/GeneralConstants';
import { ActivatedRoute } from '@angular/router';
import { SimilarCandidateDetails } from './common-interfaces-classes/SimilarCandidate';
import { HistoryQuery } from './common-interfaces-classes/HistoryQuery';
import { JobDescriptionKeys } from './common-interfaces-classes/JobDescriptionKeys';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import * as _ from "underscore";

@Injectable({
  providedIn: 'root'
})
export class JdDataService {

  private jdData: JdData;
  private jdFilterUpdateEvent: Subject<void> = new Subject();
  private jdDataEvent: Subject<JdData> = new Subject();
  private insightsEvent: Subject<any> = new Subject();
  private jdDesc: string;
  private jdDataRedirect=  new BehaviorSubject<any>({});
  private jdTIRedirect: Subject<any> = new Subject();
    private jdDataInITReport = new BehaviorSubject<string>(null);


  constructor(private sessionStorageService: SessionStorageService, private activatedRoute: ActivatedRoute) { }

  setJdData(data: JdData) { this.jdData = data; }

  clearJdData() { this.jdData = null; }

  get fetchJdData() { return this.jdData; }

  setSessionData(uniqueHash: string, redirectData: SimilarCandidateDetails) {
    this.sessionStorageService.setItem(`${GeneralConstants.LS_KEYS.JOB_DESCRIPTION}-${uniqueHash}`, JSON.stringify(redirectData));
  }

  get jdDataFromSession() {
    let redirectData: SimilarCandidateDetails = null;
    try {
      const uniqueHash = this.activatedRoute.snapshot.queryParamMap.get(GeneralConstants.LS_KEYS.UNIQUE_HASH);
      const key = `${GeneralConstants.LS_KEYS.JOB_DESCRIPTION}-${uniqueHash}`;
      redirectData = JSON.parse(this.sessionStorageService.getItem(key));
    } catch (error) {
      console.log('error while fetching jd data', error);
    }
    return redirectData;
  }

  get isComingFromJobDescription() {
    const type = this.activatedRoute.snapshot.queryParamMap.get("type");
    return type === GeneralConstants.REDIRECT_SOURCES.JOB_DESCRIPTION;
  }

  get isComingFromNLSearch() {
    const type = this.activatedRoute.snapshot.queryParamMap.get("type");
    return type === GeneralConstants.REDIRECT_SOURCES.NL_SEARCH;
  }

  getSimilarObj(jdResp: JdData) {
    const response = new SimilarCandidateDetails();

    if (!jdResp || jdResp.status === "failure" || !jdResp.jdData)
      return response;

    const jdData = jdResp.jdData;
    if (jdData['Job Title'])
      response.jobtitle.push({key: jdData['Job Title'], isMandatory: false});

    if (jdData['Suggested job Title'] && Array.isArray(jdData['Suggested job Title'])) {
      const suggestedJobTitles = this.filterAndSCFormat(jdData['Suggested job Title'], false);
      response.jobtitle = response.jobtitle.concat(suggestedJobTitles);
    }

    if (jdData['Target Company'])
        response.jobcompanyname = this.filterAndSCFormat(jdData['Target Company']);

    if (jdData.Industry)
      response.jobindustryname = this.filterAndSCFormat(jdData.Industry);

    if (jdData.School)
      response.schools = this.filterAndSCFormat(jdData.School)

    /**
     * This is the old logic for picking up the location, exact location match is the new logic
     */
    // if (this.isValidLocation(jdResp)) {
    //   const { location, continent } = this.getLocation(jdResp);
    //   response.currentlocation = this.filterAndSCFormat([location]);
    //   response.locationcontinent = continent;
    // }

    if(jdData['exactLocationMatch']){
      const exactLocationMatch = this.filterAndSCFormat([jdData['exactLocationMatch']]);
      response.currentlocation = exactLocationMatch;
    }
    //response.locationcontinent = jdResp?.jdData?.Location?.continent || [];

    if(jdData['normalizedlocations']) {
      response.currentlocation = this.filterLocationFormat(jdData['normalizedlocations']);
      response.locationContinentName = this.filterLocationNameFormat(jdData['normalizedlocations']);
    }

    if(jdData['normalizedcontinents'] && jdData['normalizedcontinents']?.length)
      response.locationcontinent = _.uniq(jdData['normalizedcontinents']) || [];


    const uniqueSkills = this.getUniqueSkills(jdResp);
    if (uniqueSkills.length)
      response.skills = this.filterAndSCFormat(uniqueSkills);

    if (jdData.Degree)
      response.degrees = this.filterAndSCFormat(jdData.Degree);

    if (jdData.mandatoryDegree)
      response.degrees = response.degrees.concat(this.filterAndSCFormat(jdData.mandatoryDegree, true));

    if (jdData['Field of study'])
      response.fieldOfStudy = this.filterAndSCFormat(jdData['Field of study']);

    if (jdData['Years of experience'] && jdData['Years of experience']['maximum experience'])
      response.toYrsOfExperinece = jdData['Years of experience']['maximum experience'];

    if (jdData['Years of experience'] && jdData['Years of experience']['minimum experience'])
      response.fromYrsOfExperience = jdData['Years of experience']['minimum experience'];

    return response;
  }

  /**
   * Get count of filters applied on JD search
   */
  get countOfJDFilterApplied() {
    const obj = this.jdDataFromSession;
    let count = 0;
    if(obj){
      Object.keys(obj).forEach(item => {
        if (Array.isArray(obj[item]) && obj[item].length) {
          count++;
        }
      });
      // Count experience if either value is not null
      if (
        obj.fromYrsOfExperience != null ||
        obj.toYrsOfExperinece != null
      ) {
        count++;
      }
    }
    return count;
  }


  private isValidLocation(resp: JdData) : boolean {
    const continent = resp?.jdData?.Location?.continent;
    const country   = resp?.jdData?.Location?.country;

    if (!continent || !country)
      return false;

    return GeneralConstants.VALID_CONTINENTS.includes(continent.trim().toLowerCase());
  }

  private getLocation(jdResp: JdData) {
    let response = "";

    const location = jdResp?.jdData?.Location;

    const city = location?.city?.toString().trim();
    const state = location?.state?.toString().trim();
    const country = location?.country?.toString().trim();

    if (city?.length)
      response = city;

    if (state?.length) {
      if (response.length) response = response + `, ${state}`;
      else response = state;
    }

    if (response.length)
      response = response + `, ${country}`;
    else
      response = country;

    return {
      location: response,
      continent: location?.continent ? location.continent : null
    };
  }

  private getUniqueSkills(jdData: JdData) : Array<string> {
    let skills = [];

    if (Array.isArray(jdData.jdData['Preferred Skills']))
      skills = skills.concat(jdData.jdData['Preferred Skills']);
    if (Array.isArray(jdData.jdData.Tools))
      skills = skills.concat(jdData.jdData.Tools);
    if (Array.isArray(jdData.jdData['Mandatory Skills']))
      skills = skills.concat(jdData.jdData['Mandatory Skills']);

    skills = skills.map(el => el.toLowerCase());

    return _.uniq(skills);
  }

  /**
   * Same function is used in history and person-search-listing redirect via talent-insights with jd filters
   */
  getSimilarObjFromHistory(query: (Partial<HistoryQuery>)) {
    const response = new SimilarCandidateDetails();
    if (!query?.job_description_keys) return response;

    const jdKeys = query.job_description_keys;
    // Job Title
    if (jdKeys?.job_titles) {
      response.jobtitle = this.filterAndSCFormat(this.splitIfString(jdKeys.job_titles));
    }
    if (jdKeys?.mandatory_job_titles) {
      response.jobtitle = response.jobtitle.concat(
        this.filterAndSCFormat(this.splitIfString(jdKeys.mandatory_job_titles), true)
      );
    }
    // Company
    if (jdKeys?.companies) {
      response.jobcompanyname = this.filterAndSCFormat(this.splitIfString(jdKeys.companies));
    }
    if (jdKeys?.mandatory_companies) {
      response.jobcompanyname = response.jobcompanyname.concat(
        this.filterAndSCFormat(this.splitIfString(jdKeys?.mandatory_companies), true)
      );
    }
    // School
    if (jdKeys?.schools) {
      response.schools = this.filterAndSCFormat(this.splitIfString(jdKeys?.schools));
    }
    if (jdKeys?.mandatory_schools) {
      response.schools = response.schools.concat(
        this.filterAndSCFormat(this.splitIfString(jdKeys?.mandatory_schools), true)
      );
    }
    // Location
    if (jdKeys?.locations) {
      response.currentlocation = this.filterAndSCFormat(this.splitIfString(jdKeys?.locations));
    }
    if (jdKeys?.mandatory_locations) {
      response.currentlocation = response.currentlocation.concat(
        this.filterAndSCFormat(this.splitIfString(jdKeys?.mandatory_locations), true)
      );
    }
    // Skills
    if (jdKeys?.skills) {
      response.skills = this.filterAndSCFormat(this.splitIfString(jdKeys.skills));
    }
    if (jdKeys?.mandatory_skills) {
      response.skills = response.skills.concat(
        this.filterAndSCFormat(this.splitIfString(jdKeys.mandatory_skills), true)
      );
    }
    // Degree
    if (jdKeys?.degrees) {
      response.degrees = this.filterAndSCFormat(this.splitIfString(jdKeys?.degrees));
    }
    if (jdKeys?.mandatory_degrees) {
      response.degrees = response.degrees.concat(
        this.filterAndSCFormat(this.splitIfString(jdKeys?.mandatory_degrees), true)
      );
    }
    // Field of study/Educational major
    if (jdKeys?.education_majors) {
      response.fieldOfStudy = this.filterAndSCFormat(this.splitIfString(jdKeys?.education_majors));
    }
    if (jdKeys?.mandatory_education_majors) {
      response.fieldOfStudy = response.fieldOfStudy.concat(
        this.filterAndSCFormat(this.splitIfString(jdKeys?.mandatory_education_majors), true)
      );
    }
    // Industries
    if (jdKeys?.industries) {
      response.jobindustryname = this.filterAndSCFormat(this.splitIfString(jdKeys?.industries));
    }
    if (jdKeys?.mandatory_industries) {
      response.jobindustryname = response.jobindustryname.concat(
        this.filterAndSCFormat(this.splitIfString(jdKeys?.mandatory_industries), true)
      );
    }

    if (jdKeys?.fromYrsOfExperience)
      response.fromYrsOfExperience = jdKeys?.fromYrsOfExperience;
    if (jdKeys?.toYrsOfExperience)
      response.toYrsOfExperinece = jdKeys?.toYrsOfExperience;

    return response;
  }

  private splitIfString(el: string[] | string) {
    if (typeof el === "string")
      return el.split(";");
    if (Array.isArray(el))
      return el;
    return [];
  }

  getJdKeys(scDetails: SimilarCandidateDetails) {
    const jdKeys = new JobDescriptionKeys();
    if(!scDetails) return;
    scDetails.jobtitle.forEach(el => {
      if (el.isMandatory && Array.isArray(jdKeys.mandatory_job_titles)) jdKeys.mandatory_job_titles.push(el.key);
      else if (Array.isArray(jdKeys.job_titles)) jdKeys.job_titles.push(el.key);
    });

    scDetails.jobcompanyname.forEach(el => {
      if (el.isMandatory && Array.isArray(jdKeys.mandatory_companies)) jdKeys.mandatory_companies.push(el.key);
      else if (Array.isArray(jdKeys.companies)) jdKeys.companies.push(el.key);
    });

    scDetails.jobindustryname.forEach(el => {
      if (el.isMandatory && Array.isArray(jdKeys.mandatory_industries)) jdKeys.mandatory_industries.push(el.key);
      else if (Array.isArray(jdKeys.industries)) jdKeys.industries.push(el.key);
    });

    scDetails.schools.forEach(el => {
      if (el.isMandatory && Array.isArray(jdKeys.mandatory_schools)) jdKeys.mandatory_schools.push(el.key);
      else if (Array.isArray(jdKeys.schools)) jdKeys.schools.push(el.key);
    });

    scDetails.currentlocation.forEach(el => {
      if (el.isMandatory && Array.isArray(jdKeys.mandatory_locations)) jdKeys.mandatory_locations.push(el.key);
      else if (Array.isArray(jdKeys.locations)) jdKeys.locations.push(el.key);
    });

    scDetails.skills.forEach(el => {
      if (el.isMandatory && Array.isArray(jdKeys.mandatory_skills)) jdKeys.mandatory_skills.push(el.key);
      else if (Array.isArray(jdKeys.skills)) jdKeys.skills.push(el.key);
    });

    scDetails.degrees.forEach(el => {
      if (el.isMandatory && Array.isArray(jdKeys.mandatory_degrees)) jdKeys.mandatory_degrees.push(el.key);
      else if (Array.isArray(jdKeys.degrees)) jdKeys.degrees.push(el.key);
    });

    scDetails.fieldOfStudy.forEach(el => {
      if (el.isMandatory && Array.isArray(jdKeys.mandatory_education_majors)) jdKeys.mandatory_education_majors.push(el.key);
      else if (Array.isArray(jdKeys.education_majors)) jdKeys.education_majors.push(el.key);
    });

    scDetails.companysize.forEach(el => {
      if (el.isMandatory && Array.isArray(jdKeys.mandatory_company_sizes)) jdKeys.mandatory_company_sizes.push(el.key);
      else if (Array.isArray(jdKeys.company_sizes)) jdKeys.company_sizes.push(el.key);
    });

    jdKeys.fromYrsOfExperience = scDetails.fromYrsOfExperience ? scDetails.fromYrsOfExperience : 0;
    jdKeys.toYrsOfExperience = scDetails.toYrsOfExperinece;

    return jdKeys;
  }

  private filterAndSCFormat(list: Array<string>, isMandatory = false) {
    return list.filter(Boolean).map(el => ({key: el, isMandatory}));
  }

  private filterLocationFormat(list: Array<any>, isMandatory = false) {
    return list.filter(Boolean).map(el => ({key: el.exactlocationmatch, isMandatory, continent: el.continent}));
  }
    private filterLocationNameFormat(list: Array<any>) {
    return list.filter(Boolean).map(el => ({key: el.exactlocationmatch, continent: el.continent, calledfrom: 'jd'}));
  }

  get jdFilterUpdateObs(): Observable<void> {
    return this.jdFilterUpdateEvent.asObservable();
  }

  jdFilterUpdateEventEmit() {
    this.jdFilterUpdateEvent.next(undefined as any);
  }

  get jdDataEventObs(): Observable<JdData> {
    return this.jdDataEvent.asObservable();
  }

  emitJDDataEvent(data: JdData) {
    this.jdDataEvent.next(data);

  }

  emitInsightsEvent(data : {action: string, tab: string, filterType?: string[]}) {
    this.insightsEvent.next(data);
  }
  getInsightsEvent(): Observable<void>{
    return this.insightsEvent.asObservable();
  }


setJdDescription(data: any) { this.jdDesc = data; }

  get fetchJdDescription() { return this.jdDesc; }


  get jdDataForRedirect(): Observable<any> {
    return this.jdDataRedirect.asObservable();
  }

  emitjdDataForRedirect(data: any) {
    this.jdDataRedirect.next(data);

  }
  clearjdData() {
    this.jdDataRedirect.next(null);
  }

get jdTIRedirectEvent(): Observable<any> {
    return this.jdTIRedirect.asObservable();
  }

  emitJDRedirectEvent(data: any) {
    this.jdTIRedirect.next(data);

  }

  get jdDataInTIReportDetails(): Observable<string> {
    return this.jdDataInITReport.asObservable();
  }

  setJDDataInTIReportDetails(data: string) {
    this.jdDataInITReport.next(data);

  }

    clearJDDataInTIReportDetails() {
    this.jdDataInITReport.next(null);
  }

}
